/**
 * Zod validation schemas for TPRA Home Dashboard API responses
 * Following existing codebase pattern from agent/components/TPRADashboard/validation/
 */

import { z } from "zod";

// Summary Response Schema
export const HomeSummaryResponseSchema = z.object({
  success: z.boolean(),
  total_assessment: z.object({
    count: z.number(),
    new_this_month: z.number(),
  }),
  overall_risk: z.object({
    score: z.number(),
    max: z.number(),
    change: z.number(),
    formula: z.string().optional(),
  }),
  monitoring_alerts: z.object({
    total: z.number(),
    breach_events: z.object({
      count: z.number(),
      new_today: z.number(),
    }),
    soc2_expired: z.object({
      count: z.number(),
      new_today: z.number(),
    }),
  }),
  pending_tasks: z.object({
    total: z.number(),
    awaiting_action: z.object({
      due_this_week: z.number(),
    }),
  }),
});

// Assessment Stages Response Schema
export const AssessmentStageTypeSchema = z.enum([
  "intake_form",
  "information_gathering",
  "generating_results",
  "review_results",
  "completed",
  "continuous_monitoring",
]);

export const AssessmentStagesResponseSchema = z.object({
  success: z.boolean(),
  stages: z.array(
    z.object({
      assessment_stage: AssessmentStageTypeSchema,
      count: z.number(),
    })
  ),
});

// Risk Heatmap Response Schema
export const RiskHeatmapResponseSchema = z.object({
  success: z.boolean(),
  matrix: z.array(
    z.object({
      risk_level: z.enum(["High", "Medium", "Low"]),
      business_criticality: z.enum(["Low", "Medium", "High", "Critical"]),
      count: z.number(),
    })
  ),
  risk_levels: z.array(z.string()),
  criticality_levels: z.array(z.string()),
});

// Findings Summary Response Schema
export const FindingsSummaryResponseSchema = z.object({
  success: z.boolean(),
  total_findings: z.number(),
  by_severity: z.object({
    critical: z.object({
      count: z.number(),
      percentage: z.number(),
    }),
    high: z.object({
      count: z.number(),
      percentage: z.number(),
    }),
    medium: z.object({
      count: z.number(),
      percentage: z.number(),
    }),
    low: z.object({
      count: z.number(),
      percentage: z.number(),
    }),
  }),
});

// Tasks Response Schema
export const TasksResponseSchema = z.object({
  success: z.boolean(),
  tasks: z.array(
    z.object({
      id: z.string(),
      agent_session_id: z.string(),
      task_type: z.enum(["intake_form_pending", "due_diligence_pending"]),
      vendor_name: z.string(),
      product_name: z.string(),
      pending_for_days: z.number().optional(),
      message: z.string(),
    })
  ),
});

// Recent Events Response Schema
// Using .nullish() for optional fields as API returns null (not undefined)
export const RecentEventsResponseSchema = z.object({
  success: z.boolean(),
  events: z.array(
    z.object({
      id: z.string(),
      vendor_name: z.string(),
      product_name: z.string(),
      vendor_logo_url: z.string().nullish(),
      title: z.string(),
      description: z.string().nullish(),
      timestamp: z.string(),
      source_url: z.string().nullish(),
    })
  ),
  total_count: z.number(),
});
